// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

export type MembershipOption = {
  id: string;
  name: string;
  isFriend?: boolean;
  // A guest the current user created earlier. Their own guests are theirs to reuse, so they are
  // offered alongside friends without needing a search.
  isMine?: boolean;
  // Null for guest/temporary players, set for registered platform users.
  userId?: string | null;
};

type MembershipRowLike =
  | { kind: 'existing'; playerId: string }
  | { kind: 'draft'; rowId: string; selectedPlayerId?: string };

type MembershipDraftStateLike = {
  query: string;
  selectedPlayerId?: string;
  temporarySelected: boolean;
  status: 'idle' | 'pending';
};

export const normalizeMembershipQuery = (value: string) => value.trim().toLowerCase();

// People the caller already knows — friends, plus guests they created themselves — are always
// offered. Typing a query widens the pool to registered platform users. Guests belonging to somebody
// else are never candidates.
export const buildMembershipCandidatePool = <T extends MembershipOption>(
  candidates: T[],
  query: string
): T[] => {
  const known = candidates.filter((option) => option.isFriend === true || option.isMine === true);
  if (!normalizeMembershipQuery(query)) return known;
  const knownIds = new Set(known.map((option) => option.id));
  return [
    ...known,
    ...candidates.filter((option) => !knownIds.has(option.id) && Boolean(option.userId)),
  ];
};

// Folds server search results into the caller's known players. Known entries win: they carry the
// isFriend/isMine flags that decide ordering and no-query visibility, which a search hit lacks.
export const mergeSearchedCandidates = <T extends MembershipOption>(known: T[], searched: T[]): T[] => {
  if (searched.length === 0) return known;
  const byId = new Map(known.map((option) => [option.id, option]));
  searched.forEach((option) => {
    if (!byId.has(option.id)) {
      byId.set(option.id, option);
    }
  });
  return Array.from(byId.values());
};

export const hasExactMembershipNameMatch = (options: MembershipOption[], query: string) => {
  const normalizedQuery = normalizeMembershipQuery(query);
  if (!normalizedQuery) return false;
  return options.some((option) => normalizeMembershipQuery(option.name) === normalizedQuery);
};

export const buildMembershipOptionsByQuery = <T extends MembershipOption>(
  options: T[],
  query: string,
  limit = 8
): T[] => {
  const normalizedQuery = normalizeMembershipQuery(query);
  return options
    .filter((option) => normalizeMembershipQuery(option.name).includes(normalizedQuery))
    .sort((left, right) => {
      const leftFriend = left.isFriend === true;
      const rightFriend = right.isFriend === true;
      if (leftFriend !== rightFriend) {
        return leftFriend ? -1 : 1;
      }
      const byName = left.name.localeCompare(right.name);
      if (byName !== 0) {
        return byName;
      }
      return left.id.localeCompare(right.id);
    })
    .slice(0, limit);
};

export const buildMembershipDraftOptions = <T extends MembershipOption>(
  rows: MembershipRowLike[],
  rowId: string,
  availableMembers: T[],
  query: string,
  selectedPlayerId?: string,
  limit = 8
): T[] => {
  const reservedIds = new Set(
    rows.flatMap((candidate) => {
      if (candidate.kind === 'existing') return [candidate.playerId];
      if (candidate.rowId !== rowId && candidate.selectedPlayerId) return [candidate.selectedPlayerId];
      return [];
    })
  );
  const availableOptions = buildMembershipCandidatePool(availableMembers, query).filter(
    (member) => !reservedIds.has(member.id) || member.id === selectedPlayerId
  );
  return buildMembershipOptionsByQuery(availableOptions, query, limit);
};

export const shouldShowTemporaryToggle = (
  draft: Pick<MembershipDraftStateLike, 'query' | 'selectedPlayerId' | 'temporarySelected'>,
  hasExactMatch: boolean
) =>
  draft.temporarySelected ||
  (draft.query.trim().length > 0 && !draft.selectedPlayerId && !hasExactMatch);

export const canConfirmMembershipDraft = (
  draft: MembershipDraftStateLike,
  saving: boolean,
  hasExactMatch: boolean
) =>
  !saving &&
  draft.status !== 'pending' &&
  (Boolean(draft.selectedPlayerId) ||
    (draft.temporarySelected && draft.query.trim().length > 0 && !hasExactMatch));
